import { useCallback, useEffect, useState } from 'react';
import { useGetDatasetPresignedUrlMutation, useUploadDatasetFilesMutation } from '~/api/v2/hooks';
import { ProgressStatus } from '~/pages/DatasetDetailsPage/types';
import { useAppDispatch } from '~/redux';
import { BannerType, closeBanner, showBanner } from '~/redux/slices/bannerSlice';
import usePreventPageReload from '../usePreventPageReload';

export interface UploadingFile {
  progress: number;
  status: ProgressStatus;
}

const useDatasetFileUploader = (datasetId: number, isCurrentTab: boolean, supportiveDocs: boolean = false) => {
  const dispatch = useAppDispatch();
  const [getDatasetPresignedUrl, { isError: isPresignedUrlError }] = useGetDatasetPresignedUrlMutation();
  const [uploadFiles, { isLoading: isFilesUploading, isError: isFileUploadError, isSuccess: isFilesLoaded }] =
    useUploadDatasetFilesMutation({});
  const [uploadingFiles, setUploadingFiles] = useState<Map<string, UploadingFile>>(new Map());

  usePreventPageReload(isFilesUploading);

  useEffect(() => {
    if (isFileUploadError || isPresignedUrlError || isFilesLoaded) {
      if (isFilesLoaded) {
        dispatch(
          showBanner({
            message: `Uploaded: ${uploadingFiles.size} of ${uploadingFiles.size} files completed`,
            type: BannerType.FILE_UPLOADED,
          }),
        );
      } else {
        dispatch(closeBanner());
      }
      setUploadingFiles(new Map());
    }
  }, [isFileUploadError, isPresignedUrlError, isFilesLoaded]);

  useEffect(() => {
    const inProgressFilesLength = [...uploadingFiles.values()].filter(
      ({ status }) => status === ProgressStatus.RUNNING,
    ).length;
    if (isCurrentTab && !isFilesLoaded && (inProgressFilesLength || isFilesUploading)) {
      const uploadedFiles = uploadingFiles.size - inProgressFilesLength;
      dispatch(
        showBanner({
          message: `Uploading: ${uploadedFiles} of ${uploadingFiles.size} files complete. Do not refresh or exit your browser window until after upload complete.`,
          type: BannerType.FILE_UPLOAD,
        }),
      );
    }
  }, [isCurrentTab, uploadingFiles, isFilesUploading, isFilesLoaded]);

  const handleUploadedFiles = useCallback(
    async (files: File[] | null) => {
      if (!files || !datasetId) return;

      const presignedUrlsResult = await Promise.allSettled(
        files.map(({ name, type }) =>
          getDatasetPresignedUrl({
            datasetId,
            supportiveDocs,
            fileName: name,
            contentType: type,
          }).unwrap(),
        ),
      );

      const successfulPresignedUrls = Object.assign(
        {},
        ...presignedUrlsResult.map((item) => (item.status === 'fulfilled' ? item.value : {})),
      );

      const filesToUpload = files.filter(({ name }) => successfulPresignedUrls[name]);

      const uploadingFilesMap = filesToUpload.reduce<Map<string, UploadingFile>>((acc, file) => {
        const fileId = `datasets/${datasetId}/${file.name}`;
        acc.set(fileId, {
          progress: 0,
          status: ProgressStatus.RUNNING,
        });

        return acc;
      }, new Map());

      setUploadingFiles(uploadingFilesMap);

      const completedFiles = new Set<string>();

      await uploadFiles(
        filesToUpload.map((file) => ({
          presignedUrl: successfulPresignedUrls[file.name],
          datasetId,
          supportiveDocs,
          file: file,
          onProgress: (progress: number) => {
            setUploadingFiles((prevFiles) => {
              const newFilesMap = new Map(prevFiles);
              const fileId = `datasets/${datasetId}/${file.name}`;
              const currentFile = newFilesMap.get(fileId);
              if (currentFile) {
                const status = progress < 100 ? ProgressStatus.RUNNING : ProgressStatus.COMPLETED;
                newFilesMap.set(fileId, {
                  progress: progress,
                  status: status,
                });

                if (progress >= 100 && !completedFiles.has(fileId)) {
                  completedFiles.add(fileId);
                }
              }

              return newFilesMap;
            });
          },
        })),
      );
    },
    [datasetId, getDatasetPresignedUrl, uploadFiles],
  );

  return {
    handleUploadedFiles,
    uploadingFiles,
    isFilesUploading,
  };
};

export default useDatasetFileUploader;
